import React from "react";
import FlexContainer from "connectui/component/layout/FlexContainer";
import ListComponent from "connectui/component/layout/list/ListComponent";
import {useTheme} from "connectui/theme/ThemeProvider";
import Text from "connectui/component/display/Text";
import ButtonText from "connectui/component/input/button/ButtonText";
import {ReactComponent as PlusIcon} from "connectui/icons/plus-text.svg";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import TextButtonBase from "connectui/component/input/button/base/TextButtonBase";
import SvgIcon from "connectui/component/display/SvgIcon";
import DropDownSelect from "connectui/component/input/dropdown/DropDownSelect";
import {buildStyles} from "connectui/component/buildStyles";
import Container from "connectui/component/layout/Container";
import {messagingSettingsStageEnum} from "ui/component/businesssettings/messaging/BaseMessagingSettingsPanel";
import {useSelector} from "react-redux";
import ToggleCardComponent from "connectui/component/display/card/ToggleCardComponent";
import PhoneInput from "connectui/component/input/text/type/PhoneInput";
import {useFeatureFlags} from "utilities/featureUtil";

const useStyles = (theme) =>
  buildStyles({
    dropdownMenuItem: {
      width: "100%",
      position: "relative",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      padding: "10px 14px",
      boxSizing: "border-box",
      textAlign: "left",
      "&:hover": {
        backgroundColor: theme.colors.hoverGray,
      },
    },
    actionRowContainer: {
      width: "100%",
      marginTop: theme.spacing(3),
    },
    contentContainer: {
      width: "100%",
      textAlign: "left",
      padding: theme.spacing(3, 4),
      gap: theme.spacing(3),
    },
    titleText: {
      textAlign: "left",
      width: "100%",
      display: "block",
    },
    descriptionText: {
      textAlign: "left",
      width: "100%",
      display: "block",
      marginBottom: theme.spacing(2),
    },
  });

const WhatsAppManagePanel = ({setStage, data}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const flags = useFeatureFlags();
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const [selectedPhone, setSelectedPhone] = React.useState(null);
  const [selectedOption, setSelectedOption] = React.useState(0);
  const [ownPhoneNumber, setOwnPhoneNumber] = React.useState("");
  const [isValidPhone, setIsValidPhone] = React.useState(false);
  const [country, setCountry] = React.useState(null);

  const whatsAppCapableManagedPhones = () => {
    // Filter SMS enabled customer
    // TODO: Fix the connected service
    if (!user_profile?.connected_services?.includes("sms")) {
      return [];
    }

    return (user_profile?.managed_phones || [])
      .filter((phone) => {
        // TODO: fix the capability check
        return (
          phone.active &&
          phone.capabilities.includes("SMS") &&
          phone.phone_type &&
          ["managed_twilio", "managed_whatsapp"].includes(phone.phone_type)
        );
      })
      .map((phone) => phone.phone);
  };
  const displayNumbers = whatsAppCapableManagedPhones();

  // Create the content for the Enso Number toggle
  const ensoNumberContent = (
    <ListComponent className={classes.contentContainer}>
      <Text variant="textMD" weight="medium" className={classes.titleText}>
        Use Enso Number
      </Text>
      <Text
        variant="textSM"
        color="textSecondary"
        className={classes.descriptionText}
      >
        Select from your existing phone numbers or purchase a new one through
        Enso. You can receive WhatsApp messages directly through Enso.
      </Text>

      {displayNumbers && displayNumbers.length > 0 ? (
        <>
          <DropDownSelect
            onChange={setSelectedPhone}
            label={
              <Text variant="textMD" color="textSecondary">
                {selectedPhone || "Select a phone number"}
              </Text>
            }
            sx={{width: "100%"}}
          >
            {whatsAppCapableManagedPhones().map((text, index) => (
              <Container
                key={index}
                className={classes.dropdownMenuItem}
                value={text}
              >
                <Text variant="textMD" color="textPrimary">
                  {text}
                </Text>
              </Container>
            ))}
          </DropDownSelect>
        </>
      ) : (
        <Text weight="medium" color="error500">
          To connect a phone number to WhatsApp, you would need to purchase a
          new phone number.
        </Text>
      )}

      <ButtonIconText
        buttonBase={TextButtonBase}
        typographyVariant="textSM"
        weight="semibold"
        color="primary"
        label="Buy a New Phone Number"
        sx={{width: "fit-content", marginTop: theme.spacing(2)}}
        onClick={() =>
          setStage({
            id: messagingSettingsStageEnum.phoneEdit,
            data: {type: "managed"},
            overrideBackToStageId: messagingSettingsStageEnum.whatsApp,
          })
        }
        icon={
          <SvgIcon
            component={PlusIcon}
            sx={{
              color: theme.colors.primary,
              paddingRight: theme.spacing(1),
            }}
          />
        }
      />
    </ListComponent>
  );

  // Create the content for the Own Number toggle
  const ownNumberContent = flags.twilioWhatsAppByopImplementation && (
    <ListComponent className={classes.contentContainer}>
      <Text variant="textMD" weight="medium" className={classes.titleText}>
        Use Your Own Number
      </Text>
      <Text
        variant="textSM"
        color="textSecondary"
        className={classes.descriptionText}
      >
        Your number will be shown in WhatsApp communications. Use this if you
        prefer to handle WhatsApp messaging with your personal or business
        number.
      </Text>

      <PhoneInput
        phone={ownPhoneNumber}
        onChange={setOwnPhoneNumber}
        setValidPhone={setIsValidPhone}
        setCountry={setCountry}
        edit={true}
        sx={{width: "100%"}}
      />
    </ListComponent>
  );

  return (
    <FlexContainer
      flexDirection={"column"}
      alignItems={"flex-start"}
      justifyContent={"center"}
      gap={theme.spacing(5)}
      sx={{paddingBottom: theme.spacing(3), width: "100%", maxWidth: "660px"}}
    >
      <Text variant="displayXS" weight="semibold">
        Select a Phone Number
      </Text>
      <Text variant={"textSM"} sx={{color: theme.colors.textSecondary}}>
        You'll need a phone number to send and receive messages on WhatsApp.
        Choose an existing number, enter your own, or purchase a new one.
      </Text>

      <ListComponent sx={{textAlign: "left", width: "100%"}}>
        <ToggleCardComponent
          isSelected={selectedOption === 0}
          onToggle={() => setSelectedOption(0)}
          customContent={ensoNumberContent}
          sx={{marginBottom: theme.spacing(3)}}
        />
        {flags.twilioWhatsAppByopImplementation && (
          <ToggleCardComponent
            isSelected={selectedOption === 1}
            onToggle={() => setSelectedOption(1)}
            customContent={ownNumberContent}
          />
        )}
      </ListComponent>

      <ButtonText
        label={"Continue"}
        typographyVariant="textSM"
        weight="semibold"
        disabled={
          (selectedOption === 0 && !selectedPhone) ||
          (selectedOption === 1 && !isValidPhone)
        }
        sx={{
          width: "fit-content",
          padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        }}
        color="white"
        onClick={() => {
          if (selectedOption === 0) {
            setStage({
              id: messagingSettingsStageEnum.whatsAppEmbedded,
              data: {selectedPhone, type: "managed"},
            });
          } else {
            setStage({
              id: messagingSettingsStageEnum.whatsAppEmbedded,
              data: {
                selectedPhone: ownPhoneNumber,
                type: "whatsapp_display_only",
              },
            });
          }
        }}
      />
    </FlexContainer>
  );
};

export default WhatsAppManagePanel;
